/**
 * @fileoverview O formulário das telas de entrada (entrar, criar conta, esqueci
 * a senha, nova senha, confirmar o e-mail): título, campos com contraste real
 * no tema claro e anel de foco de 1px, o botão principal (o azul da marca) e
 * os avisos. Os links levam a cor com !important (também no :hover e no
 * :visited): o public/index.html pinta todo <a> de verde.
 */

import { makeStyles } from "@material-ui/core/styles";

export const useAuthFormStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";
  const faint = "#71717A";
  const danger = dark ? "#FCA5A5" : "#B91C1C";
  const focus = dark ? "rgba(91,124,255,0.6)" : "rgba(61,92,253,0.55)";
  // Azul bebê no escuro; no claro, o mesmo azul mais fundo (o bebê some no branco).
  const linkHover = dark ? "#89CFF0" : "#0277BD";
  const inputBg = dark ? "#141416" : "#FFFFFF";
  const reduced = "@media (prefers-reduced-motion: reduce)";
  return {
    view: { animation: "$viewIn 200ms ease-out", [reduced]: { animation: "none" } },
    title: { margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: "-0.5px", lineHeight: 1.25, color: text },
    subtitle: { margin: "8px 0 28px", fontSize: 14.5, lineHeight: 1.55, color: muted },
    field: { marginBottom: 16 },
    label: { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: text },
    inputWrap: { position: "relative" },
    input: {
      width: "100%", height: 44, padding: "0 14px", fontSize: 15, fontFamily: "inherit", borderRadius: 10, boxSizing: "border-box",
      border: `1px solid ${dark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.18)"}`, background: inputBg, color: text,
      outline: "none", transition: "border-color 150ms, box-shadow 150ms",
      "&::placeholder": { color: faint },
      "&:focus": { borderColor: focus, boxShadow: `0 0 0 1px ${focus}` },
      // Preenchido pelo navegador, o mesmo campo: o App.js pinta todo input preenchido
      // de cinza e herda o canto do pai (aqui, quadrado), e a sombra dele tira o anel de foco.
      "&:-webkit-autofill, &:-webkit-autofill:hover": {
        borderRadius: 10, caretColor: text,
        WebkitBoxShadow: `0 0 0 100px ${inputBg} inset !important`, WebkitTextFillColor: `${text} !important`,
      },
      "&:-webkit-autofill:focus": { WebkitBoxShadow: `0 0 0 100px ${inputBg} inset, 0 0 0 1px ${focus} !important` },
      "&[aria-invalid='true']": { borderColor: danger },
    },
    inputWithButton: { paddingRight: 46 },
    eye: {
      position: "absolute", right: 6, top: 6, width: 32, height: 32, border: "none", borderRadius: 8, background: "transparent",
      color: muted, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
      "&:hover": { color: text, background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)" },
      "&:focus-visible": { outline: `1px solid ${focus}` },
    },
    error: { fontSize: 12.5, color: danger, marginTop: 6 },
    hint: { fontSize: 12.5, color: faint, marginTop: 6 },
    row: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, margin: "2px 0 22px", flexWrap: "wrap" },
    check: {
      display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: text, cursor: "pointer", userSelect: "none",
      "& input": { width: 16, height: 16, margin: 0, accentColor: dark ? "#E4E4E7" : "#18181B", cursor: "pointer" },
      "& input:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 2 },
    },
    link: {
      color: `${text} !important`, fontSize: 14, fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer",
      background: "none", border: "none", padding: 0, fontFamily: "inherit", transition: "color 150ms",
      "&:visited": { color: `${text} !important` },
      "&:hover": { color: `${linkHover} !important`, textDecorationThickness: 2 },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 2, borderRadius: 4 },
    },
    button: {
      width: "100%", height: 46, border: "none", borderRadius: 10, background: "#1c65ce", color: "#FFFFFF", fontSize: 15, fontWeight: 600,
      fontFamily: "inherit", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      transition: "background 150ms",
      "&:hover:not(:disabled)": { background: "#1050ab" },
      "&:disabled": { opacity: 0.65, cursor: "default" },
      "&:focus-visible": { outline: `2px solid ${focus}`, outlineOffset: 2 },
    },
    // O botão principal como link (o contato da Sttelar): sem a cor e o sublinhado dos links (a regra global dos links usa !important, como o `link` acima).
    linkButton: { "&, &:link, &:visited, &:hover": { color: "#FFFFFF !important", textDecoration: "none" } },
    secondary: {
      width: "100%", height: 44, borderRadius: 10, background: "transparent", color: text, fontSize: 14.5, fontWeight: 600, fontFamily: "inherit",
      border: `1px solid ${dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.18)"}`, cursor: "pointer", marginTop: 12,
      "&:hover:not(:disabled)": { background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)" },
      "&:disabled": { opacity: 0.6, cursor: "default" },
      "&:focus-visible": { outline: `1px solid ${focus}`, outlineOffset: 2 },
    },
    alt: { marginTop: 24, fontSize: 14, color: muted, display: "flex", flexWrap: "wrap", gap: 6, alignItems: "baseline" },
    notice: {
      padding: "14px 16px", borderRadius: 10, fontSize: 14.5, lineHeight: 1.55, color: text, marginBottom: 20,
      background: dark ? "rgba(255,255,255,0.04)" : "#F4F4F5", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "#E4E4E7"}`,
      // Logo depois do título ("Confira seu e-mail"), sem o subtítulo no meio: o respiro dele.
      "$title + &": { marginTop: 16 },
    },
    noticeOk: { background: dark ? "rgba(34,197,94,0.08)" : "#F0FDF4", borderColor: dark ? "rgba(34,197,94,0.2)" : "#BBF7D0" },
    noticeBad: { background: dark ? "rgba(239,68,68,0.08)" : "#FEF2F2", borderColor: dark ? "rgba(239,68,68,0.22)" : "#FECACA" },
    // A prova de pessoa só ocupa espaço nos raros casos em que pede um clique.
    humanCheck: { "& > div": { marginBottom: 8 } },
    spinner: { color: "inherit" },
    "@keyframes viewIn": { from: { opacity: 0, transform: "translateY(4px)" }, to: { opacity: 1, transform: "none" } },
  };
});
